import Button from '@carvana/ds-button';
import { TriangleAlert } from 'lucide-react';
import { icon } from '@carvana/ds-styles';
import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventedModal, ModalNames } from '|/app/modals';
import { rollBack } from '|/app/services';
import { FormStateContext } from '|/context';
import { isNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { clearScopedSlotPreference } from '|/hooks/use-slot-preference';
import useSubmit from '|/hooks/use-submit';
import styles from './edit-order-modal.module.css';

interface EditOrderModalDSProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryPreference?: string | null;
  editingSectionName?: string;
  targetStep?: string;
  currentSectionName?: string;
}

const EditOrderModal = ({
  isOpen,
  onClose,
  deliveryPreference = null,
  editingSectionName = '',
  targetStep,
  currentSectionName
}: EditOrderModalDSProps) => {
  const { trackEvent } = useAnalyticsContext();
  const { isSubmitting } = useContext(FormStateContext);
  const submit = useSubmit();
  const navigate = useNavigate();

  const [, , , route] = window.location.pathname.split('/');

  const deliveryOrPickup = deliveryPreference?.toLowerCase().includes('delivery')
    ? 'delivery'
    : 'pickup';
  const isOrderPlaced = route === 'order-placed';

  const handleConfirmEdit = useCallback(() => {
    if (isOrderPlaced) {
      trackEvent('v2_opd_edit_order_button_tap');
    }

    if (currentSectionName === 'Finalize Purchase') {
      trackEvent('v2_finalize_purchase_edit_button_clicked');
    }

    const abortController = new AbortController();

    submit(
      rollBack({
        cancelTransfer: true
      }).then(() => {
        clearScopedSlotPreference();

        // Fires from the rollback continuation, not the tap: native relaunches
        // FullPurchaseWebView on receipt, so the rollback must land first. Returns without
        // navigating — this webview is native's OPD surface, and routing it into the purchase
        // flow would take it outside OPD's boundaries. Source is 'opd', not this repo's usual
        // 'purchase' — that's what native matches on. Closes the modal because native may keep
        // this webview alive; leaving it open lets the customer re-confirm and emit again.
        if (isOrderPlaced && isNativeWebview()) {
          sendNativeEvent({
            event: MobileWebviewEvents.EDIT_ORDER,
            source: 'opd'
          });
          onClose();
          return;
        }

        if (targetStep) {
          const returnUrl = encodeURIComponent(`/steps/${targetStep}`);
          navigate(`/next-step?returnUrl=${returnUrl}`, { replace: true });
          return;
        }

        navigate('/purchase', { replace: true });
      }),
      abortController.signal
    );
  }, [submit, trackEvent, currentSectionName, targetStep, navigate, isOrderPlaced, onClose]);

  const handleModalClose = useCallback(() => {
    trackEvent('pp_edit_order_modal_closed');
    onClose();
  }, [onClose, trackEvent]);

  const handleGoBack = useCallback(() => {
    trackEvent('pp_edit_order_go_back');
    onClose();
  }, [onClose, trackEvent]);

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.EditOrder}
      modalHeader="Edit order"
      onClose={handleModalClose}
      desktopWidth="480px"
      mobileStyle="full"
    >
      <div className={styles.content}>
        <div className={styles.alertSection}>
          <div className={styles.alertHeader}>
            <TriangleAlert className={styles.alertIcon} color={icon.warning} size={48} />
            <h3 className={styles.alertTitle}>
              {isOrderPlaced
                ? `This will release your ${deliveryOrPickup} date`
                : 'Are you sure you want to edit your order?'}
            </h3>
          </div>
          <p className={styles.alertDescription}>
            {isOrderPlaced ? (
              <>
                Editing your order will release your {deliveryOrPickup} appointment and{' '}
                <strong>
                  your vehicle will become available for others to purchase if you don&apos;t
                  schedule a new appointment before the timer expires
                </strong>
              </>
            ) : (
              'You will have a limited time to make your changes, otherwise your vehicle will become unlocked and available for others to purchase.'
            )}
          </p>
        </div>
      </div>

      <div className={styles.stickyButtonContainer}>
        <div className={styles.footer}>
          <Button
            type="button"
            variant="tertiary"
            size="md"
            onClick={handleConfirmEdit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            data-analytics-id="pp_edit_modal_edit"
            data-analytics-attributes={JSON.stringify({
              target: editingSectionName.toLowerCase().replace(/\s+/g, '_')
            })}
          >
            {isOrderPlaced ? 'Release delivery date' : 'Yes, edit'}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleGoBack}
            disabled={isSubmitting}
            data-analytics-id="pp_edit_modal_cancel"
            data-analytics-attributes={JSON.stringify({
              target: editingSectionName.toLowerCase().replace(/\s+/g, '_')
            })}
          >
            {isOrderPlaced ? 'Go back' : 'No, go back'}
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default EditOrderModal;
